<article class="feed-item" tabindex="-1" [attr.aria-labelledby]="headingId">
  <h3 class="sr-only" [id]="headingId">{{ heading }}</h3>
  <div class="feed-item__main">
    <!-- A page's label above the header, e.g. the community profile's "Pinned". -->
    <ng-content select="[feedItemLabel]"></ng-content>
    <header class="feed-item__header">
      <!-- Projects, campaigns and courses have no person to link to; their title links to the item already. -->
      <div class="feed-item__byline">
        @if (isSocial) {
          <a class="feed-item__author" [uiSref]="authorRoute?.state" [uiParams]="authorRoute?.params">
            <ng-container *ngTemplateOutlet="authorBody"></ng-container>
          </a>
        } @else {
          <span class="feed-item__author">
            <ng-container *ngTemplateOutlet="authorBody"></ng-container>
          </span>
        }
        @if (item.type !== 'referral_campaign') {
          <!-- Outside the author link, so the date can be the post's own link (its permalink). -->
          <span class="feed-item__meta">
            {{ typeLabel(item) }} <span aria-hidden="true">&middot;</span>&ngsp;
            @if (item.type === 'topic' && item.category) {
              <ut-category-link [category]="item.category"></ut-category-link>&ngsp;<span aria-hidden="true">&middot;</span>&ngsp;
            }
            @if (route && !detail) {
              <a class="feed-item__date" [uiSref]="route.state" [uiParams]="route.params">{{ item.published_at | relativeDate }}<span class="sr-only">, {{ heading }}</span></a>
            } @else {
              {{ item.published_at | relativeDate }}
            }
          </span>
        }
      </div>
      <ng-template #authorBody>
        @if (authorGlyph) {
          <span class="feed-item__glyph" aria-hidden="true"><span class="material-icons">{{ authorGlyph }}</span></span>
        } @else {
          <ut-avatar-badges [user]="item.user" aria-hidden="true"></ut-avatar-badges>
        }
        <span class="feed-item__author-text">
          <span class="feed-item__author-name">
            {{ authorName }}
            @if (authorSubtext) {
              <span class="feed-item__author-subtext">{{ authorSubtext }}</span>
            }
          </span>
        </span>
      </ng-template>
      <div class="feed-item__actions" role="group" [attr.aria-label]="'Options for ' + heading">
        <!-- A page's own action, e.g. the community profile's Pin (see FeedItemExtraAction). -->
        <div class="feed-item__wide-action feed-item__extra-action">
          <ng-content select="[feedItemAction]"></ng-content>
        </div>
        @if (canSubscribe) {
          <ut-community-subscribe-action class="feed-item__wide-action" [item]="item"></ut-community-subscribe-action>
        }
        @if (canReport) {
          <ut-community-report-action class="feed-item__wide-action" [item]="item"></ut-community-report-action>
        }
        @if (hasMenu || hasPhoneMenu) {
          <button type="button" class="feed-item__menu-trigger" [class.feed-item__phone-only]="!hasMenu" [attr.aria-label]="optionsLabel" [cdkMenuTriggerFor]="statusMenu">
            <span class="material-icons" aria-hidden="true">more_vert</span>
          </button>
          <ng-template #statusMenu>
            <div class="feed-item__menu" cdkMenu>
              <!-- Phones: the labelled actions above are hidden, so they're offered here instead. -->
              @if (extraAction && isPhone) {
                <button type="button" class="feed-item__menu-item feed-item__phone-only" cdkMenuItem (cdkMenuItemTriggered)="extraAction.trigger()">
                  <svg class="feed-item__menu-svg" viewBox="0 -960 960 960" aria-hidden="true"><path fill="currentColor" [attr.d]="extraAction.menuIconPath"/></svg>
                  {{ extraAction.menuLabel }}
                </button>
              }
              @if (subscribeAction && isPhone) {
                <button type="button" class="feed-item__menu-item feed-item__phone-only" cdkMenuItem (cdkMenuItemTriggered)="subscribeAction.toggle()">
                  <span class="material-icons" aria-hidden="true">{{ subscribeAction.subscribed ? 'notifications_off' : 'notifications_none' }}</span>
                  {{ subscribeAction.subscribed ? 'Unsubscribe' : 'Subscribe' }}
                </button>
              }
              @if (reportAction && !reportAction.reported && isPhone) {
                <button type="button" class="feed-item__menu-item feed-item__phone-only" cdkMenuItem (cdkMenuItemTriggered)="reportAction.report()">
                  <span class="material-icons" aria-hidden="true">outlined_flag</span>
                  Report
                </button>
              }
              @if (canHide) {
                <button type="button" class="feed-item__menu-item" cdkMenuItem (cdkMenuItemTriggered)="toggleHidden()">
                  <span class="material-icons" aria-hidden="true">{{ item.state === 'hidden' ? 'visibility' : 'visibility_off' }}</span>
                  {{ item.state === 'hidden' ? 'Show post' : 'Hide post' }}
                </button>
              }
              @if (canEdit) {
                <button type="button" class="feed-item__menu-item" cdkMenuItem (cdkMenuItemTriggered)="editing.set(true)">
                  <span class="material-icons" aria-hidden="true">edit</span>
                  Edit status
                </button>
              }
              @if (canDelete) {
                <button type="button" class="feed-item__menu-item feed-item__menu-item--danger" cdkMenuItem (cdkMenuItemTriggered)="confirmDelete()">
                  <span class="material-icons" aria-hidden="true">delete_outline</span>
                  Delete status
                </button>
              }
            </div>
          </ng-template>
        }
      </div>
    </header>

    @if (editing()) {
      <ut-community-status-form
        [content]="item.content"
        [attachedUrl]="item.attached_url"
        [authorUserType]="item.user?.user_type"
        submitLabel="Save"
        [cancellable]="true"
        [busy]="saving()"
        (submitted)="saveStatus($event)"
        (cancelled)="editing.set(false)"></ut-community-status-form>
    } @else {
      @if (item.state === 'hidden') {
        <p class="feed-item__hidden-note">
          <span class="material-icons" aria-hidden="true">visibility_off</span>
          This post is hidden from the community
        </p>
      }
      <div class="feed-item__collapse" [class.is-collapsed]="item.state === 'hidden'" [attr.inert]="item.state === 'hidden' ? '' : null">
      <div class="feed-item__body">
        @if (titleHtml && route) {
          <a class="feed-item__title" [uiSref]="route.state" [uiParams]="route.params" [innerHTML]="titleHtml"></a>
        }
        <!-- A page's details between the title and the preview, e.g. a project's start date and countries. -->
        <ng-content select="[feedItemMeta]"></ng-content>
        <!-- Clicking a status's text opens it, as in the legacy feed; the date link is the keyboard route. -->
        <p #content tabindex="-1" class="feed-item__content" [class.feed-item__content--lead]="!titleHtml" [class.feed-item__content--status]="item.type === 'status'" [class.feed-item__content--expanded]="expanded()" [class.feed-item__content--link]="opensOnClick" [innerHTML]="preview" (click)="openFromText($event)"></p>
        @if (isClamped() && !expanded()) {
          @if (item.type === 'status') {
            <button type="button" class="feed-item__see-more" (click)="expand()">See more</button>
          } @else if (route) {
            <a class="feed-item__see-more" [uiSref]="route.state" [uiParams]="route.params">See more</a>
          }
        }
        @if (item.type === 'status' && item.attached_url) {
          <ut-status-attachment class="feed-item__attachment" [status]="item"></ut-status-attachment>
        }
      </div>
      </div>
    }

    @if (isSocial) {
      <div class="feed-item__social">
        <ut-community-likers [likeable]="item" [user]="user" (showAll)="showLikes()"></ut-community-likers>
        @if (latestReply && !showComments() && !detail && commentable) {
          <div class="feed-item__reply">
            <ut-avatar-badges class="feed-item__reply-avatar" [user]="latestReply.user" aria-hidden="true"></ut-avatar-badges>
            <div class="feed-item__reply-bubble">
              <div class="feed-item__reply-header">
                <span class="feed-item__reply-author">{{ latestReply.user.name }}</span>
                <span class="feed-item__reply-date">{{ latestReply.published_at | relativeDate }}</span>
              </div>
              <div class="feed-item__reply-content" [innerHTML]="latestReply.html"></div>
            </div>
          </div>
          @if (commentCount(item) > 1) {
            <button type="button" class="feed-item__view-comments" (click)="showMoreComments()">Show more comments</button>
          }
        }
      </div>
      <footer class="feed-item__footer" role="group" [attr.aria-label]="'Actions for ' + heading">
        <ut-community-like-action [likeable]="item"></ut-community-like-action>
        @if (!detail && commentable) {
          <button type="button" class="feed-item__action" [attr.aria-expanded]="showCommentForm()" (click)="toggleComments()">
            <span class="material-icons" aria-hidden="true">chat_bubble_outline</span>
            Add comment
          </button>
        }
        <!-- Counts at a glance, as on the legacy feed card. -->
        @if (likeCount(item) || (commentable && commentCount(item))) {
          <span class="feed-item__counts">
            @if (likeCount(item)) {
              <button type="button" class="feed-item__count" aria-haspopup="dialog" (click)="showLikes()">{{ likeCount(item) | utNumToK }} {{ likeCount(item) === 1 ? 'like' : 'likes' }}</button>
            }
            @if (commentable && commentCount(item)) {
              <button type="button" class="feed-item__count" [attr.aria-expanded]="showComments()" (click)="showMoreComments()">{{ commentCount(item) | utNumToK }} {{ commentCount(item) === 1 ? 'comment' : 'comments' }}</button>
            }
          </span>
        }
      </footer>
    }
  </div>

  @if (showComments()) {
    <ut-community-feed-item-comments [item]="item" [user]="user" [loadMoreOnOpen]="loadMoreOnOpen()" [showForm]="showCommentForm()" (commented)="onCommented()"></ut-community-feed-item-comments>
  }
</article>
